import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Link as LinkIcon,
  List, ListOrdered, Heading2, Heading3, Quote, Undo, Redo, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Palette, Ban,
} from 'lucide-react';

const ToolbarButton = ({ onClick, active, disabled, label, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    data-testid={`rte-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`inline-flex items-center justify-center h-8 w-8 rounded-md text-sm transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? 'bg-[#26247B] text-white' : 'text-black/70 hover:bg-[#EEF0F6]'
    }`}
  >
    {children}
  </button>
);

// Brand + a few practical accent shades for headings/subtitles/emphasis text.
const COLOR_SWATCHES = [
  { label: 'Navy (Brand)', value: '#26247B' },
  { label: 'Green (Brand)', value: '#2E8B57' },
  { label: 'Amber (Brand)', value: '#B4790A' },
  { label: 'Charcoal', value: '#1A1A1A' },
  { label: 'Slate Gray', value: '#5B6472' },
  { label: 'Ruby Red', value: '#B91C3C' },
  { label: 'Royal Purple', value: '#6D28D9' },
  { label: 'Teal', value: '#0F766E' },
];

const ColorPicker = ({ editor }) => {
  const [customColor, setCustomColor] = useState('#26247B');
  const currentColor = editor.getAttributes('textStyle').color;

  const applyColor = (hex) => {
    editor.chain().focus().setColor(hex).run();
  };

  const clearColor = () => {
    editor.chain().focus().unsetColor().run();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          aria-label="Text Color"
          data-testid="rte-text-color"
          className={`inline-flex items-center justify-center h-8 w-8 rounded-md text-sm transition-colors duration-150 ${
            currentColor ? 'bg-[#EEF0F6]' : 'text-black/70 hover:bg-[#EEF0F6]'
          }`}
        >
          <span className="relative">
            <Palette className="h-4 w-4" />
            <span
              className="absolute -bottom-1 left-0 right-0 h-1 rounded-full"
              style={{ backgroundColor: currentColor || '#26247B' }}
            />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-3"
        data-testid="rte-text-color-popover"
        onMouseDown={(e) => e.preventDefault()}
      >
        <p className="text-xs font-semibold text-black/60 mb-2">Text Color</p>
        <div className="grid grid-cols-4 gap-2 mb-3">
          {COLOR_SWATCHES.map((swatch) => (
            <button
              key={swatch.value}
              type="button"
              title={swatch.label}
              data-testid={`rte-color-swatch-${swatch.value.replace('#', '')}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyColor(swatch.value)}
              className={`h-8 w-8 rounded-full border-2 transition-transform duration-150 hover:scale-110 ${
                currentColor === swatch.value ? 'border-[#26247B] ring-2 ring-offset-1 ring-[#26247B]/40' : 'border-white/60'
              }`}
              style={{ backgroundColor: swatch.value }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="color"
            value={customColor}
            data-testid="rte-color-custom-input"
            onMouseDown={(e) => e.preventDefault()}
            onChange={(e) => {
              setCustomColor(e.target.value);
              applyColor(e.target.value);
            }}
            className="h-8 w-10 rounded border border-[#E6E8EE] cursor-pointer bg-transparent p-0"
          />
          <span className="text-xs text-black/60">Custom color</span>
        </div>
        <button
          type="button"
          data-testid="rte-color-clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={clearColor}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-[#E6E8EE] px-3 py-1.5 text-xs font-semibold text-black/70 hover:bg-[#F6F7F9] transition-colors duration-150"
        >
          <Ban className="h-3.5 w-3.5" /> Remove Color
        </button>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Rich text / visual editor for blog content. Supports pasting from Word/Google Docs
 * and preserves formatting (bold, italic, headings, lists, links) as clean HTML.
 * Outputs & accepts an HTML string via value/onChange, matching the existing
 * BlogManager `body` field contract (server stores raw HTML).
 */
export const RichTextEditor = ({ value, onChange, placeholder = 'Write your article…' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
    editorProps: {
      attributes: {
        class: 'rte-content',
        'data-testid': 'admin-blog-rich-text-content',
      },
    },
  });

  // Keep editor content in sync if `value` changes externally (e.g. switching posts)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="rounded-lg border border-[#E6E8EE] bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#26247B]">
      <div className="flex flex-wrap items-center gap-1 border-b border-[#E6E8EE] bg-[#F6F7F9] px-2 py-1.5" data-testid="admin-blog-rte-toolbar">
        <ToolbarButton label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Strike" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough className="h-4 w-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ToolbarButton label="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ToolbarButton label="Bullet List" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Ordered List" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ToolbarButton label="Align Left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeft className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Align Center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenter className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Align Right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRight className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Align Justify" active={editor.isActive({ textAlign: 'justify' })} onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
          <AlignJustify className="h-4 w-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ColorPicker editor={editor} />
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ToolbarButton label="Link" active={editor.isActive('link')} onClick={setLink}>
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
        <span className="w-px h-5 bg-[#E6E8EE] mx-1" />
        <ToolbarButton label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className="rte-editor" />
    </div>
  );
};
